import React, { useState } from 'react'
import { Download } from 'lucide-react'
import usePracticeStore from '../../stores/practiceStore'
import { TileType } from './TilePalette'
import jsPDF from 'jspdf'

const PracticeDownload = () => {
  const [showModal, setShowModal] = useState(false)
  const { tiles, notes, sessionName } = usePracticeStore()

  const openModal = () => {
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
  }

  const handleDownloadPDF = () => {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20
    let yPosition = margin

    // Helper function to add a new page if needed
    const checkNewPage = (requiredHeight) => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        doc.addPage()
        yPosition = margin
        return true
      }
      return false
    }

    // Helper to get tile colors (RGB values)
    const getTileColors = (type) => {
      switch (type) {
        case TileType.DRILL:
          return { border: [34, 197, 94], bg: [240, 253, 244] } // green-500, green-50
        case TileType.SCRIMMAGE:
          return { border: [59, 130, 246], bg: [239, 246, 255] } // blue-500, blue-50
        case TileType.SPEAKING_POINTS:
          return { border: [168, 85, 247], bg: [250, 245, 255] } // purple-500, purple-50
        case TileType.FITNESS:
          return { border: [249, 115, 22], bg: [255, 247, 237] } // orange-500, orange-50
        default:
          return { border: [156, 163, 175], bg: [249, 250, 251] } // gray-400, gray-50
      }
    }

    // Title
    doc.setFontSize(24)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(17, 24, 39) // gray-900
    doc.text(sessionName || 'Practice Session', margin, yPosition)
    yPosition += 12

    // Date
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(75, 85, 99) // gray-600
    const date = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
    doc.text(`Date: ${date}`, margin, yPosition)
    yPosition += 15

    // Practice Activities Section
    if (tiles.length > 0) {
      checkNewPage(30)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(17, 24, 39) // gray-900
      doc.text('Practice Activities', margin, yPosition)
      yPosition += 10

      const sortedTiles = [...tiles].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      
      sortedTiles.forEach((tile, index) => {
        checkNewPage(40)
        
        const colors = getTileColors(tile.type)
        const tileStartY = yPosition - 3
        
        // Calculate description height first
        let descriptionHeight = 0
        let descriptionLines = []
        if (tile.description) {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = tile.description
          const plainText = tempDiv.textContent || tempDiv.innerText || ''
          
          if (plainText.trim()) {
            descriptionLines = doc.splitTextToSize(plainText, pageWidth - margin * 2 - 15)
            descriptionHeight = descriptionLines.length * 5 + 10 // 5 per line + label + padding
          }
        }
        
        // Calculate links height (support both old 'link' and new 'links' array)
        // Get links array once for reuse
        const tileLinks = tile.links || (tile.link ? [tile.link] : [])
        let linksHeight = 0
        if (tileLinks.length > 0) {
          tileLinks.forEach(link => {
            const linkText = `Link: ${link}`
            const linkLines = doc.splitTextToSize(linkText, pageWidth - margin * 2 - 15)
            linksHeight += linkLines.length * 5 + 8 // 5 per line + padding per link
          })
        }
        
        // Calculate linked play height
        let playLinkHeight = 0
        if (tile.playGifUrl) {
          const playText = `Linked Play: ${tile.playName || 'Play'} (GIF: ${tile.playGifUrl})`
          const playLines = doc.splitTextToSize(playText, pageWidth - margin * 2 - 15)
          playLinkHeight = playLines.length * 5 + 8 // 5 per line + padding
        }
        
        const tileHeight = 35 + descriptionHeight + linksHeight + playLinkHeight // Base height + description + links + play link
        
        // Draw tile background box (draw once with final height)
        doc.setFillColor(colors.bg[0], colors.bg[1], colors.bg[2])
        doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2])
        doc.setLineWidth(1.5)
        doc.rect(margin, tileStartY, pageWidth - margin * 2, tileHeight, 'FD')
        
        // Now draw all text on top of the box
        // Activity header
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(17, 24, 39) // gray-900
        const activityTitle = `${index + 1}. ${tile.title || 'Untitled'}`
        doc.text(activityTitle, margin + 5, yPosition + 5)

        // Time range
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(75, 85, 99) // gray-600
        const timeText = `${tile.startTime || 0}-${tile.endTime || 0} minutes (${tile.duration || 10} min duration)`
        doc.text(timeText, margin + 5, yPosition + 12)

        // Type badge
        const typeLabels = {
          [TileType.DRILL]: 'Drill',
          [TileType.SCRIMMAGE]: 'Scrimmage',
          [TileType.SPEAKING_POINTS]: 'Speaking Points',
          [TileType.FITNESS]: 'Fitness'
        }
        const typeLabel = typeLabels[tile.type] || tile.type
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(colors.border[0], colors.border[1], colors.border[2])
        doc.text(`Type: ${typeLabel}`, margin + 5, yPosition + 18)

        // Description (if exists)
        let currentTextY = yPosition + 24
        if (descriptionLines.length > 0) {
          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(75, 85, 99) // gray-600
          doc.text('Description:', margin + 5, currentTextY)
          currentTextY += 5
          
          descriptionLines.forEach((line, lineIndex) => {
            checkNewPage(6)
            doc.text(line, margin + 10, currentTextY + (lineIndex * 5))
          })
          currentTextY += descriptionLines.length * 5 + 3
        }

        // Links (if exist) - support both old 'link' and new 'links' array
        if (tileLinks.length > 0) {
          tileLinks.forEach((link, linkIndex) => {
            checkNewPage(6)
            doc.setFontSize(9)
            doc.setFont('helvetica', 'normal')
            doc.setTextColor(59, 130, 246) // blue-500
            const linkText = `Link ${linkIndex + 1}: ${link}`
            const linkLines = doc.splitTextToSize(linkText, pageWidth - margin * 2 - 10)
            linkLines.forEach((line, lineIndex) => {
              checkNewPage(6)
              doc.text(line, margin + 5, currentTextY + (lineIndex * 5))
            })
            currentTextY += linkLines.length * 5 + 3
          })
        }

        // Linked Play (if exists)
        if (tile.playGifUrl) {
          checkNewPage(6)
          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(59, 130, 246) // blue-500
          const playText = `Linked Play: ${tile.playName || 'Play'} (GIF: ${tile.playGifUrl})`
          const playLines = doc.splitTextToSize(playText, pageWidth - margin * 2 - 10)
          playLines.forEach((line, lineIndex) => {
            checkNewPage(6)
            doc.text(line, margin + 5, currentTextY + (lineIndex * 5))
          })
          currentTextY += playLines.length * 5 + 3
        }

        yPosition += tileHeight + 8
      })
    }

    // Notes section
    if (notes && notes.trim()) {
      checkNewPage(30)
      const notesStartY = yPosition - 3
      
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = notes
      const plainText = tempDiv.textContent || tempDiv.innerText || ''
      
      // Calculate content height
      let notesContentHeight = 15 // Header + padding
      if (plainText.trim()) {
        const lines = doc.splitTextToSize(plainText, pageWidth - margin * 2 - 10)
        notesContentHeight += lines.length * 5 + 5
      }
      
      // Draw section box
      doc.setFillColor(255, 255, 255)
      doc.setDrawColor(229, 231, 235) // gray-200
      doc.setLineWidth(1)
      doc.rect(margin, notesStartY, pageWidth - margin * 2, notesContentHeight, 'FD')
      
      // Draw text on top
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(17, 24, 39) // gray-900
      doc.text('Notes', margin + 5, yPosition + 5)
      yPosition += 10

      if (plainText.trim()) {
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(75, 85, 99) // gray-600
        const lines = doc.splitTextToSize(plainText, pageWidth - margin * 2 - 10)
        lines.forEach((line) => {
          checkNewPage(6)
          doc.text(line, margin + 5, yPosition)
          yPosition += 5
        })
      }
      
      yPosition += 5
    }

    // Practice Summary section
    if (tiles.length > 0) {
      checkNewPage(40)
      const sortedTiles = [...tiles].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      const totalDuration = sortedTiles.length > 0 
        ? (sortedTiles[sortedTiles.length - 1].endTime || 0) + 1
        : 0

      // Calculate duration by type
      const durationByType = sortedTiles.reduce((acc, tile) => {
        const type = tile.type || 'unknown'
        const duration = tile.duration || 10
        acc[type] = (acc[type] || 0) + duration
        return acc
      }, {})

      const typeLabels = {
        [TileType.DRILL]: 'Drills',
        [TileType.SCRIMMAGE]: 'Scrimmages',
        [TileType.SPEAKING_POINTS]: 'Speaking Points',
        [TileType.FITNESS]: 'Fitness'
      }

      // Draw summary box
      doc.setFillColor(255, 255, 255)
      doc.setDrawColor(229, 231, 235) // gray-200
      doc.setLineWidth(1)
      const summaryBoxHeight = 30 + (Object.keys(durationByType).length * 6)
      const summaryStartY = yPosition - 3
      doc.rect(margin, summaryStartY, pageWidth - margin * 2, summaryBoxHeight, 'FD')

      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(17, 24, 39) // gray-900
      doc.text('Practice Summary', margin + 5, yPosition + 5)
      yPosition += 10

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(75, 85, 99) // gray-600
      
      // Total Activities
      doc.setFont('helvetica', 'bold')
      doc.text('Total Activities:', margin + 5, yPosition)
      doc.setFont('helvetica', 'normal')
      doc.text(`${sortedTiles.length}`, pageWidth - margin - 30, yPosition)
      yPosition += 7

      // Total Duration
      doc.setFont('helvetica', 'bold')
      doc.text('Total Duration:', margin + 5, yPosition)
      doc.setFont('helvetica', 'normal')
      doc.text(`${totalDuration} minutes`, pageWidth - margin - 30, yPosition)
      yPosition += 10

      // Duration by Type
      doc.setFont('helvetica', 'bold')
      doc.text('Duration by Type:', margin + 5, yPosition)
      yPosition += 7

      Object.entries(durationByType)
        .sort(([a], [b]) => {
          const order = [TileType.DRILL, TileType.SCRIMMAGE, TileType.SPEAKING_POINTS, TileType.FITNESS]
          return order.indexOf(a) - order.indexOf(b)
        })
        .forEach(([type, duration]) => {
          doc.setFont('helvetica', 'normal')
          doc.text(`${typeLabels[type] || type}:`, margin + 10, yPosition)
          doc.text(`${duration} minutes`, pageWidth - margin - 30, yPosition)
          yPosition += 6
        })
    }

    // Save the PDF
    const fileName = `${sessionName || 'practice-session'}.pdf`
    doc.save(fileName)
    closeModal()
  }

  return (
    <>
      <button
        onClick={openModal}
        className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
      >
        <Download size={14} />
        <span>Download</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Download</h3>
            
            <div className="space-y-4">
              <div className="p-3 border rounded flex items-center justify-between">
                <div>
                  <div className="font-medium">PDF</div>
                  <div className="text-xs text-gray-500">Export practice session as PDF</div>
                </div>
                <button
                  onClick={handleDownloadPDF}
                  className="px-3 py-1 rounded bg-indigo-500 text-white hover:bg-indigo-600"
                >
                  Download
                </button>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PracticeDownload


